// src/components/views/PublicProfileView.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Grid, MapPin, ArrowLeft, MessageCircle, 
  UserPlus, UserCheck, Lock, MoreHorizontal, Loader2, 
  Share, Share2, Ban, Flag, CheckCircle, X, Copy, Search, Send, Heart, ShoppingBag, User, Play,
  Trash2, Star, ChevronLeft, ChevronRight, Layers, Image as ImageIcon, Bookmark
} from 'lucide-react';
import { 
  doc, getDoc, collection, query, where, getDocs, updateDoc, 
  arrayUnion, arrayRemove, addDoc, serverTimestamp, orderBy, 
  onSnapshot, setDoc, deleteDoc, increment 
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { UserProfile, Product } from '../../types';
// ❌ REMOVED: import { ThriftProductDetailModal } from '../shopping/ThriftProductDetailModal';

// ============================================================================
// HELPERS (unchanged)
// ============================================================================
const AuthorInfo = ({ authorId, fallbackName, fallbackImage, isOwner, onDelete }: any) => {
    const [author, setAuthor] = useState({ name: fallbackName || 'User', image: fallbackImage });

    useEffect(() => {
        if (!authorId) return;
        const fetchAuthor = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', authorId));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setAuthor({ name: data.displayName || data.username || fallbackName || 'User', image: data.photoURL || data.photoUrl || data.avatar || fallbackImage });
                }
            } catch (e) { console.error(e); }
        };
        fetchAuthor();
    }, [authorId, fallbackName, fallbackImage]);

    return (
        <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3 text-left group flex-1">
                <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full lg:rounded-none bg-zinc-900 flex items-center justify-center text-white font-bold text-sm overflow-hidden shrink-0 border border-zinc-800">
                    {author.image ? <img src={author.image} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" alt="Author"/> : <User size={18}/>}
                </div>
                <div className="min-w-0">
                    <p className="text-sm font-black text-white uppercase tracking-widest leading-none truncate group-hover:underline underline-offset-4">{author.name}</p>
                    <p className="text-[10px] text-zinc-500 mt-1 font-mono uppercase tracking-widest">Network</p>
                </div>
            </div>
            {isOwner && (
                <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-2 text-zinc-600 hover:text-red-500 transition-colors rounded-full lg:rounded-none">
                    <Trash2 size={16} />
                </button>
            )}
        </div>
    );
};

const ImageCarousel = ({ images, title, onDoubleTap }: { images: string[], title: string, onDoubleTap?: () => void }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [showStar, setShowStar] = useState(false);
    const imageList = (images && images.length > 0) ? images : ["https://placehold.co/800x800/111/444?text=NO+SIGNAL"];

    const handleScroll = () => {
        if (scrollRef.current) {
            const index = Math.round(scrollRef.current.scrollLeft / scrollRef.current.clientWidth);
            setCurrentIndex(index);
        }
    };

    const scrollTo = (index: number) => {
        if (scrollRef.current) scrollRef.current.scrollTo({ left: index * scrollRef.current.clientWidth, behavior: 'smooth' });
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onDoubleTap) {
            setShowStar(true);
            onDoubleTap();
            setTimeout(() => setShowStar(false), 1000);
        }
    };

    return (
        <div className="relative w-full h-full group bg-zinc-950 overflow-hidden cursor-pointer" onDoubleClick={handleDoubleClick}>
            <div ref={scrollRef} onScroll={handleScroll} className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                {imageList.map((img, i) => (
                    <img key={i} src={img} className="w-full h-full object-cover shrink-0 snap-center select-none grayscale group-hover:grayscale-0 transition-all duration-700" alt={`${title} ${i + 1}`} />
                ))}
            </div>

            {showStar && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
                    <Star size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
                </div>
            )}

            {imageList.length > 1 && (
                <>
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5 z-20 pointer-events-none">
                        {imageList.map((_, idx) => (<div key={idx} className={`h-1.5 transition-all ${idx === currentIndex ? 'bg-white w-6 rounded-full lg:rounded-none' : 'bg-white/40 w-1.5 rounded-full lg:rounded-none'}`} />))}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex > 0 ? currentIndex - 1 : imageList.length - 1); }} className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronLeft size={20}/></button>
                    <button onClick={(e) => { e.stopPropagation(); scrollTo(currentIndex < imageList.length - 1 ? currentIndex + 1 : 0); }} className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 text-white items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity border border-white/20 backdrop-blur-md hover:bg-white hover:text-black rounded-none"><ChevronRight size={20}/></button>
                </>
            )}
        </div>
    );
};

// ============================================================================
// POST MODAL (for normal images/videos/text)
// ============================================================================
const ProfilePostModal = ({ post, isOpen, onClose, currentUser, onSelectProduct }: any) => {
    const [comments, setComments] = useState<any[]>([]);
    const [newComment, setNewComment] = useState('');
    const [loading, setLoading] = useState(false);
    
    const [isLiked, setIsLiked] = useState(post?.likes?.includes(currentUser?.uid) || false);
    const [likeCount, setLikeCount] = useState(post?.likes?.length || 0);
    const [copied, setCopied] = useState(false);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [showHeart, setShowHeart] = useState(false);

    useEffect(() => {
        if (post) {
            setIsLiked(post?.likes?.includes(currentUser?.uid) || false);
            setLikeCount(post?.likes?.length || 0);
        }
    }, [post, currentUser]);

    useEffect(() => {
        if (!post?.id || !isOpen) return;
        const q = query(collection(db, "posts", post.id, "comments"), orderBy("createdAt", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setComments(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, [post?.id, isOpen]);

    const handleShare = async () => {
        if (navigator.share) {
            try { await navigator.share({ title: `Check out my fit`, url: `https://scoop.app/post/${post.id}` }); } 
            catch (err) { console.log("Share cancelled"); }
        } else {
            navigator.clipboard.writeText(`https://scoop.app/post/${post.id}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const handleSendComment = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!newComment.trim() || !currentUser?.uid || !post?.id) return;
        setLoading(true);
        try {
            await addDoc(collection(db, "posts", post.id, "comments"), {
                text: newComment, userId: currentUser.uid, userName: currentUser.displayName || 'User',
                userAvatar: currentUser.photoURL || currentUser.avatar || '', createdAt: serverTimestamp()
            });
            await updateDoc(doc(db, "posts", post.id), { comments: increment(1) });
            setNewComment('');
        } catch (e) { console.error(e); } finally { setLoading(false); }
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!window.confirm("Delete your reply?")) return;
        try {
            await deleteDoc(doc(db, "posts", post.id, "comments", commentId));
            await updateDoc(doc(db, "posts", post.id), { comments: increment(-1) });
        } catch (e) { console.error("Error deleting comment", e); }
    };

    const handleLikeToggle = async () => {
        if (!currentUser?.uid || !post?.id) return;
        const postRef = doc(db, "posts", post.id);
        const originallyLiked = isLiked;
        
        setIsLiked(!originallyLiked);
        setLikeCount((prev: number) => originallyLiked ? prev - 1 : prev + 1);

        try {
            if (originallyLiked) {
                await updateDoc(postRef, { likes: arrayRemove(currentUser.uid) });
            } else {
                await updateDoc(postRef, { likes: arrayUnion(currentUser.uid) });
            }
        } catch (e) {
            setIsLiked(originallyLiked);
            setLikeCount((prev: number) => originallyLiked ? prev + 1 : prev - 1);
        }
    };

    const handleSaveItem = async () => {
        if (!currentUser?.uid || !post?.id) return;
        const userRef = doc(db, "users", currentUser.uid);
        const currentArray = currentUser?.savedPosts || [];
        try {
            if (currentArray.includes(post.id)) {
                await updateDoc(userRef, { savedPosts: arrayRemove(post.id) });
            } else {
                await updateDoc(userRef, { savedPosts: arrayUnion(post.id) });
            }
        } catch(e) { console.error(e); }
    };

    const handleSingleClick = (e: React.MouseEvent) => {
        if (e.detail === 1) {
            if (videoRef.current?.paused) {
                videoRef.current.play();
                setIsPlaying(true);
            } else {
                videoRef.current?.pause();
                setIsPlaying(false);
            }
        }
    };

    const handleDoubleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowHeart(true);
        if (!post.likes?.includes(currentUser?.uid)) {
            handleLikeToggle();
        }
        setTimeout(() => setShowHeart(false), 1000);
    };

    if (!isOpen || !post) return null;

    let items = post.outfitItems || post.items || post.taggedItems || [];
    if (items.length === 0 && post.taggedProductName) {
        items = [{
            id: post.taggedProductId || Math.random().toString(), name: post.taggedProductName,
            image: post.taggedProductImage || post.image, price: post.taggedProductPrice || 0, brand: 'Tagged Item'
        }];
    }
    const validItems = items.filter((i: any) => i && (i.name || i.image));

    const isVideo = post.contentType === 'hook' || post.videoUrl || post.video;
    const displayMedia = isVideo ? (post.videoUrl || post.video) : (post.image || (post.images && post.images[0]) || post.imageUrl || post.img);
    const isOwner = currentUser?.uid === post?.authorId || currentUser?.uid === post?.userId || currentUser?.uid === post?.uid;
    const isSaved = currentUser?.savedPosts?.includes(post.id);

    // For thrift posts, we now just show them as regular posts (no special thrift modal)
    const isThriftPost = post.type === 'thrift' || post.type === 'thrift_item';

    if (isThriftPost) {
        return (
            <div className="fixed inset-0 z-[800] bg-black/90 backdrop-blur-md flex justify-center items-center animate-in fade-in p-4 sm:p-6 overflow-y-auto">
                <div className="w-full max-w-xl bg-black border border-zinc-800 rounded-2xl flex flex-col max-h-full shadow-2xl relative my-auto" onClick={(e) => e.stopPropagation()}>
                    <button onClick={onClose} className="absolute top-4 right-4 p-2 text-zinc-500 hover:text-white transition-colors z-10 bg-black rounded-full border border-zinc-800 hover:border-zinc-600">
                        <X size={20} />
                    </button>
                    
                    <div className="flex-1 overflow-y-auto scrollbar-hide p-6">
                        <div className="flex items-center justify-between mb-6 pr-10">
                            <AuthorInfo 
                                authorId={post.authorId || post.userId || post.uid} 
                                fallbackName={post.authorName || post.userName} 
                                fallbackImage={post.authorImage || post.userAvatar} 
                                isOwner={false}
                            />
                        </div>
                        
                        {/* Thrift item display */}
                        {post.image || post.images?.[0] ? (
                            <div className="aspect-square bg-zinc-950 rounded-xl overflow-hidden mb-4 border border-zinc-800">
                                <img src={post.image || post.images?.[0]} className="w-full h-full object-cover" alt={post.itemName || post.title} />
                            </div>
                        ) : null}
                        
                        <h3 className="text-xl font-black text-white uppercase tracking-tighter mb-2">
                            {post.itemName || post.title || 'Thrift Item'}
                        </h3>
                        
                        {post.price && (
                            <p className="text-lg font-black text-white font-mono mb-2">R {post.price}</p>
                        )}
                        
                        {post.condition && (
                            <p className="text-sm text-zinc-400 mb-2">Condition: {post.condition}</p>
                        )}
                        
                        {post.size && (
                            <p className="text-sm text-zinc-400 mb-2">Size: {post.size}</p>
                        )}
                        
                        {post.description && (
                            <p className="text-sm text-zinc-300 mt-3 leading-relaxed">{post.description}</p>
                        )}
                        
                        <div className="flex gap-6 py-4 border-y border-zinc-800 text-zinc-500 text-sm font-bold mt-4">
                            <span><strong className="text-white">{likeCount}</strong> Likes</span>
                            <span><strong className="text-white">{post.comments || 0}</strong> Replies</span>
                        </div>

                        <div className="flex justify-between py-2 border-b border-zinc-900">
                            <button onClick={handleLikeToggle} className="p-3 text-zinc-500 hover:text-white transition-colors flex items-center gap-2 group">
                                <Heart size={20} className={isLiked ? "fill-white text-white" : "group-hover:text-white"} />
                            </button>
                            <button className="p-3 text-zinc-500 hover:text-white transition-colors cursor-default">
                                <MessageCircle size={20} />
                            </button>
                            <button onClick={handleSaveItem} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                <Bookmark size={20} fill={isSaved ? "white" : "none"} className={isSaved ? "text-white" : ""} />
                            </button>
                            <button onClick={handleShare} className="p-3 text-zinc-500 hover:text-white transition-colors">
                                {copied ? <CheckCircle size={20} className="text-green-500"/> : <Share2 size={20} />}
                            </button>
                        </div>

                        <div className="mt-6 space-y-6">
                            {comments.length > 0 ? comments.map((c, i) => (
                                <div key={i} className="flex gap-4 group">
                                    <div className="w-10 h-10 rounded-full bg-zinc-900 overflow-hidden shrink-0 border border-zinc-800 flex items-center justify-center">
                                        {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover"/> : <User size={18} className="text-zinc-500"/>}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-white font-bold text-sm">{c.userName || 'User'}</p>
                                            {c.userId === currentUser?.uid && (
                                                <button onClick={() => handleDeleteComment(c.id)} className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                        <p className="text-zinc-300 text-sm mt-1 leading-relaxed font-sans">{c.text}</p>
                                    </div>
                                </div>
                            )) : <p className="text-zinc-600 text-sm font-mono uppercase tracking-widest text-center py-6 border border-dashed border-zinc-900 rounded-xl">Be the first to reply</p>}
                        </div>
                    </div>
                    
                    <div className="p-4 border-t border-zinc-800 bg-zinc-950 shrink-0 rounded-b-2xl">
                        <form onSubmit={handleSendComment} className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-zinc-800 overflow-hidden shrink-0 border border-zinc-700 flex items-center justify-center">
                                {currentUser?.photoURL || currentUser?.avatar ? <img src={currentUser.photoURL || currentUser.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" /> : <User size={16} className="text-zinc-500"/>}
                            </div>
                            <input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Post your reply..." className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-zinc-600 font-sans" />
                            <button type="submit" disabled={!newComment.trim() || loading} className="px-5 py-2 bg-white text-black font-black uppercase tracking-widest text-[10px] rounded-full disabled:opacity-50 hover:bg-zinc-200 transition-colors">
                                {loading ? <Loader2 size={14} className="animate-spin"/> : 'Reply'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[300] bg-black flex flex-col lg:flex-row animate-in fade-in duration-300">
            <div className="w-full lg:w-[60%] h-[50vh] lg:h-full bg-zinc-950 border-b lg:border-b-0 lg:border-r border-zinc-800 relative flex items-center justify-center overflow-hidden">
                <button onClick={onClose} className="absolute top-6 left-6 z-50 p-3 bg-black/50 hover:bg-white hover:text-black text-white rounded-full lg:rounded-none backdrop-blur-md transition-colors border border-white/20">
                    <ArrowLeft size={24}/>
                </button>
                
                {isVideo ? (
                    <div className="relative w-full h-full cursor-pointer group" onClick={handleSingleClick} onDoubleClick={handleDoubleClick}>
                        <video 
                            ref={videoRef}
                            src={displayMedia} 
                            className="w-full h-full object-contain bg-black" 
                            autoPlay 
                            loop 
                            playsInline 
                        />
                        {!isPlaying && (
                             <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/10">
                                 <div className="w-20 h-20 bg-black/50 backdrop-blur-md rounded-full lg:rounded-none flex items-center justify-center text-white border border-white/20">
                                     <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[16px] border-l-white border-b-[10px] border-b-transparent ml-2"></div>
                                 </div>
                             </div>
                        )}
                        {showHeart && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
                                <Heart size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="relative w-full h-full cursor-pointer" onDoubleClick={handleDoubleClick}>
                        <ImageCarousel images={post.images || [post.image]} title={post.title || post.caption} />
                        {showHeart && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30 animate-in zoom-in duration-300 fade-out delay-500">
                                <Heart size={120} className="text-white fill-white drop-shadow-[0_0_40px_rgba(0,0,0,0.5)]" />
                            </div>
                        )}
                    </div>
                )}
            </div>
            
            <div className="w-full lg:w-[40%] flex flex-col h-[50vh] lg:h-full bg-black">
                <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full lg:rounded-none overflow-hidden bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 font-bold text-lg uppercase">
                            {post.authorImage || post.userPhoto || post.avatar ? <img src={post.authorImage || post.userPhoto || post.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" alt=""/> : (post.authorName || post.userName || 'U').charAt(0)}
                        </div>
                        <div><h4 className="text-white font-black uppercase text-sm">{post.authorName || post.userName}</h4></div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-hide">
                    <div>
                        <h2 className="text-2xl font-black text-white italic uppercase tracking-tighter mb-2">{post.type === 'thrift' ? post.itemName : (post.title || "Fit Check")}</h2>
                        <p className="text-zinc-400 text-sm leading-relaxed">{post.description || post.caption}</p>
                    </div>

                    {validItems.length > 0 && (
                        <div className="border-t border-zinc-800 pt-8">
                            <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2"><ShoppingBag size={14}/> Tagged Pieces</h3>
                            <div className="grid grid-cols-2 gap-4">
                                {validItems.map((item: any, idx: number) => (
                                    <button key={idx} onClick={() => { onClose(); onSelectProduct && onSelectProduct(item); }} className="text-left group border border-zinc-800 p-2 rounded-xl lg:rounded-none hover:border-white transition-colors">
                                        <div className="w-full aspect-square bg-zinc-900 overflow-hidden mb-2 relative rounded-lg lg:rounded-none">
                                            <img src={item.image} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt=""/>
                                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-black tracking-widest uppercase">View</div>
                                        </div>
                                        <p className="text-white text-[10px] font-bold truncate mb-0.5">{item.name}</p>
                                        <p className="text-zinc-500 text-[9px] uppercase tracking-wider truncate">{item.brand}</p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="border-t border-zinc-800 pt-8">
                        <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-4 flex items-center gap-2"><MessageCircle size={14}/> Comments</h3>
                        <div className="space-y-6">
                            {comments.length === 0 ? <p className="text-zinc-600 text-xs italic">No comments yet. Start the conversation.</p> : comments.map(c => (
                                <div key={c.id} className="flex gap-3 group">
                                    <div className="w-8 h-8 rounded-full lg:rounded-none bg-zinc-900 flex items-center justify-center text-zinc-500 font-bold text-xs uppercase overflow-hidden shrink-0 border border-zinc-800">
                                        {c.userAvatar ? <img src={c.userAvatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale" alt=""/> : c.userName?.charAt(0) || 'U'}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between">
                                            <p className="text-white text-sm"><span className="font-bold mr-2 uppercase text-xs tracking-widest">{c.userName}</span><span className="text-zinc-400">{c.text}</span></p>
                                            {c.userId === currentUser?.uid && (
                                                <button onClick={() => handleDeleteComment(c.id)} className="text-zinc-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Trash2 size={12} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="border-t border-zinc-800 p-6 bg-black safe-area-bottom">
                    <div className="flex gap-4 mb-4">
                        <button onClick={handleLikeToggle} className="text-white hover:text-zinc-400 transition-transform active:scale-90 flex items-center gap-2">
                            <Heart size={24} fill={isLiked ? "currentColor" : "none"} className={isLiked ? "text-white" : ""} />
                            <span className="text-sm font-bold">{likeCount}</span>
                        </button>
                        <button onClick={handleSaveItem} className="text-white hover:text-zinc-400 transition-transform active:scale-90 flex items-center gap-2">
                            <Bookmark size={24} fill={isSaved ? "currentColor" : "none"} className={isSaved ? "text-white" : ""} />
                        </button>
                        <button onClick={handleShare} className="text-white hover:text-zinc-400 transition-transform active:scale-90 flex items-center gap-2">
                            <Share2 size={24} />
                        </button>
                    </div>
                    <form onSubmit={handleSendComment} className="flex items-center gap-2 bg-zinc-900/50 border border-zinc-800 p-2 rounded-xl lg:rounded-none focus-within:border-white transition-colors">
                        <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Add a comment..." className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-zinc-600 px-2" />
                        <button type="submit" disabled={!newComment.trim() || loading} className="text-white font-black text-[10px] uppercase tracking-widest disabled:opacity-30 hover:text-zinc-400 px-4 py-2">
                            {loading ? <Loader2 size={14} className="animate-spin" /> : 'Post'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// MAIN PUBLIC PROFILE COMPONENT
// ============================================================================
interface PublicProfileViewProps {
  viewingProfile: UserProfile; 
  currentUser?: UserProfile | null; 
  products?: Product[]; 
  onBack: () => void;
  onFollow: (uid: string) => void;
  onUnfollow: (uid: string) => void;
  onMessage: (uid: string) => void;
  onSelectProduct?: (product: any) => void; 
}

export const PublicProfileView: React.FC<PublicProfileViewProps> = ({
  viewingProfile: initialProfile, 
  currentUser,
  onBack,
  onFollow,
  onUnfollow,
  onMessage,
  onSelectProduct
}) => {
  const [profile, setProfile] = useState<UserProfile>(initialProfile);
  const [userPosts, setUserPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSeller, setIsSeller] = useState(false);
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShareSheetOpen, setIsShareSheetOpen] = useState(false);
  const [shareSearch, setShareSearch] = useState('');
  const [shareUsers, setShareUsers] = useState<any[]>([]);
  const [actionFeedback, setActionFeedback] = useState<string | null>(null);
  
  const [activePost, setActivePost] = useState<any | null>(null);
  const [hasRequested, setHasRequested] = useState(false);

  useEffect(() => {
    if (!initialProfile?.uid) return; 

    const fetchRealData = async () => {
      setLoading(true);
      try {
        const userDocRef = doc(db, 'users', initialProfile.uid);
        const userSnap = await getDoc(userDocRef);
        if (userSnap.exists()) setProfile(userSnap.data() as UserProfile);

        const postsQuery = query(collection(db, 'posts'), where('authorId', '==', initialProfile.uid));
        const postsSnap = await getDocs(postsQuery);
        
        let posts: any[] = [];
        if (postsSnap.empty) {
            const fallbackQuery = query(collection(db, 'posts'), where('userId', '==', initialProfile.uid));
            const fallbackSnap = await getDocs(fallbackQuery);
            posts = fallbackSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } else {
            posts = postsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        }
        setUserPosts(posts);
        
        // Check if user is a seller (has at least one thrift post)
        const hasThrift = posts.some(p => p.type === 'thrift' || p.type === 'thrift_item');
        setIsSeller(hasThrift);
        
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
      setLoading(false);
    };
    fetchRealData();
  }, [initialProfile?.uid]);

  // 🟢 FIX: Silent fail for follow request query (permissions error)
  useEffect(() => {
      if (!currentUser?.uid || !profile?.uid) return;
      const checkPendingRequest = async () => {
          try {
              const q = query(
                  collection(db, 'notifications'),
                  where('userId', '==', profile.uid),
                  where('senderId', '==', currentUser.uid),
                  where('type', '==', 'follow_request'),
                  where('status', '==', 'pending')
              );
              const snap = await getDocs(q);
              setHasRequested(!snap.empty);
          } catch (error) {
              // Permission denied – fallback to no pending request
              setHasRequested(false);
          }
      };
      checkPendingRequest();
  }, [currentUser?.uid, profile?.uid]);

  useEffect(() => {
      if (isShareSheetOpen && currentUser?.uid) { 
          const fetchShareUsers = async () => {
              const q = query(collection(db, 'users'), where('uid', '!=', currentUser.uid));
              const snap = await getDocs(q);
              const list = snap.docs.map(d => d.data()).filter((u: any) => 
                  u.isBrand !== true && u.accountType !== 'brand' && u.accountType !== 'business' && u.role !== 'brand'
              );
              setShareUsers(list);
          };
          fetchShareUsers();
      }
  }, [isShareSheetOpen, currentUser?.uid]);

  const handleSendProfileToUser = async (recipientId: string, recipientName: string) => {
      if (!currentUser?.uid) return; 
      try {
          const chatId = [currentUser.uid, recipientId].sort().join("_");
          await setDoc(doc(db, "chats", chatId), {
              participants: [currentUser.uid, recipientId],
              updatedAt: serverTimestamp(),
              lastMessage: `Shared ${profile.displayName}'s profile`,
              hidden: { [currentUser.uid]: false, [recipientId]: false }
          }, { merge: true });

          await addDoc(collection(db, "chats", chatId, "messages"), {
              text: `Check out ${profile.displayName}'s profile!`,
              senderId: currentUser.uid, 
              createdAt: serverTimestamp(), 
              messageType: 'profile_share', 
              profile: {
                  id: profile.uid, 
                  displayName: profile.displayName, 
                  photoURL: profile.photoURL || (profile as any).photoUrl || (profile as any).avatar || '',
                  isBrand: !!(profile as any).isBrand
              }
          });
          showFeedback(`Sent to ${recipientName}`);
      } catch (error) { console.error("Failed to share", error); }
  };

  const handleCopyLink = () => {
      navigator.clipboard.writeText(`${window.location.origin}/user/${profile.uid}`);
      showFeedback("Link Copied!");
      setIsMenuOpen(false);
  };

  const handleBlock = async () => {
      if(!currentUser?.uid || !window.confirm(`Block ${profile.displayName}?`)) return; 
      try {
          await updateDoc(doc(db, 'users', currentUser.uid), { blockedUsers: arrayUnion(profile.uid) });
          showFeedback("User Blocked");
          onBack(); 
      } catch (error) {}
  };

  const handleReport = async () => {
      if (!currentUser?.uid) return; 
      const reason = window.prompt("Reason for reporting:");
      if(!reason) return;
      try {
          await addDoc(collection(db, 'reports'), { targetId: profile.uid, reporterId: currentUser.uid, reason, type: 'user_report', createdAt: serverTimestamp() });
          showFeedback("Report Sent");
          setIsMenuOpen(false);
      } catch (error) {}
  };

  const handleFollowAction = async () => {
      if (!currentUser?.uid) return;
      if (profile.isPrivate) {
          setHasRequested(true);
          showFeedback("Request Sent");
          await addDoc(collection(db, 'notifications'), {
              userId: profile.uid, senderId: currentUser.uid, senderName: currentUser.displayName || 'User',
              senderPhoto: currentUser.photoURL || (currentUser as any).photoUrl || (currentUser as any).avatar || '', type: 'follow_request', status: 'pending', title: 'Follow Request',
              message: `${currentUser.displayName || 'A user'} wants to follow you.`, createdAt: serverTimestamp(), read: false
          });
      } else {
          onFollow(profile.uid);
      }
  };

  const handleCancelRequest = async () => {
      if (!currentUser?.uid) return;
      setHasRequested(false);
      showFeedback("Request Canceled");
      const q = query(collection(db, 'notifications'), where('userId', '==', profile.uid), where('senderId', '==', currentUser.uid), where('type', '==', 'follow_request'), where('status', '==', 'pending'));
      const snap = await getDocs(q);
      snap.forEach((docSnap) => { deleteDoc(doc(db, 'notifications', docSnap.id)); });
  };

  const showFeedback = (msg: string) => { setActionFeedback(msg); setTimeout(() => setActionFeedback(null), 2000); };

  const isMe = currentUser && profile.uid === currentUser.uid;
  const isFollowing = currentUser?.followingUsers?.includes(profile.uid);
  const isPrivate = profile.isPrivate;
  const canViewContent = !isPrivate || isMe || isFollowing;
  const followerCount = profile.followersCount ?? (Array.isArray(profile.followers) ? profile.followers.length : 0);
  const followingCount = Array.isArray(profile.followingUsers) ? profile.followingUsers.length : (profile.following || 0);

  const filteredShareUsers = shareUsers.filter(u => 
      u.displayName?.toLowerCase().includes(shareSearch.toLowerCase()) || u.handle?.toLowerCase().includes(shareSearch.toLowerCase())
  );

  const handlePostClick = (post: any) => {
    setActivePost(post);
  };

  const renderGridItems = (posts: any[]) => {
      return posts.map((post) => {
          const isVideo = post.contentType === 'hook' || post.videoUrl || post.video;
          const mediaSrc = isVideo ? (post.videoUrl || post.video) : (post.image || (post.images && post.images[0]) || post.imageUrl || post.img);

          if (post.type === 'thought') {
              return (
                  <div key={post.id} onClick={() => handlePostClick(post)} className={`aspect-square relative group overflow-hidden border border-zinc-900 lg:border-zinc-800 lg:rounded-none cursor-pointer flex flex-col justify-center items-center p-4 text-center ${post.bgColor || 'bg-zinc-900'}`}>
                      <p className="text-white font-sans text-xs sm:text-sm font-medium line-clamp-4 leading-relaxed px-2">{post.caption}</p>
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex items-center justify-center gap-6 text-white font-black">
                          <div className="flex items-center gap-2"><Heart size={20} className={post.likes?.includes(currentUser?.uid) ? "text-white fill-white" : ""}/> {post.likes?.length || 0}</div>
                          <div className="flex items-center gap-2"><MessageCircle size={20}/> {post.comments || 0}</div>
                      </div>
                  </div>
              );
          }

          return (
              <div key={post.id} onClick={() => handlePostClick(post)} className="aspect-square bg-zinc-950 relative group overflow-hidden border border-zinc-900 lg:border-zinc-800 lg:rounded-none cursor-pointer" >
                  {isVideo ? (
                      <>
                          <video src={mediaSrc} className="w-full h-full object-cover transition-all duration-500 grayscale group-hover:grayscale-0 lg:group-hover:scale-105 pointer-events-none" muted loop playsInline autoPlay />
                          <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-md rounded-none px-1.5 py-1 border border-zinc-800">
                              <Play size={14} className="text-white"/>
                          </div>
                      </>
                  ) : (
                      <>
                          <img src={mediaSrc} className="w-full h-full object-cover transition-all duration-500 grayscale hover:grayscale-0 lg:group-hover:scale-105" alt="Fit" />
                          {post.images && post.images.length > 1 && (
                              <div className="absolute top-2 right-2 bg-black/80 backdrop-blur-md rounded-none px-1.5 py-1 border border-zinc-800">
                                  <Layers size={14} className="text-white"/>
                              </div>
                          )}
                          {post.type === 'thrift' && (
                              <div className="absolute bottom-2 left-2 bg-white text-black px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest shadow-md">
                                  R {post.price}
                              </div>
                          )}
                      </>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex items-center justify-center gap-6 text-white font-black z-20">
                      <div className="flex items-center gap-2"><Heart size={20} className={post.likes?.includes(currentUser?.uid) ? 'fill-white text-white' : 'text-white'} /> {post.likes?.length || 0}</div>
                  </div>
              </div>
          );
      });
  };

  if (loading) return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-[250] animate-in fade-in">
          <Loader2 size={32} className="text-white animate-spin mb-4" />
          <p className="text-zinc-500 text-xs font-mono uppercase tracking-widest">Loading Profile</p>
      </div>
  );

  const isGhost = (profile as any).accountType === 'ghost' || (profile as any).role === 'ghost';
  if (isGhost && !isMe) {
      return (
          <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-[250] animate-in fade-in">
              <button onClick={onBack} className="absolute top-6 lg:top-10 left-6 z-50 p-3 bg-zinc-900 hover:bg-white hover:text-black text-white rounded-full lg:rounded-none transition-colors flex items-center gap-2">
                  <ArrowLeft size={24} /> <span className="hidden lg:inline font-bold uppercase tracking-widest text-xs">Back</span>
              </button>
              <div className="w-32 h-32 rounded-full border-2 border-zinc-800 bg-zinc-950 flex items-center justify-center text-zinc-600 mb-6">
                  <User size={48} />
              </div>
              <h1 className="text-3xl font-black text-white uppercase italic tracking-tighter mb-2">Shopper</h1>
              <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest flex items-center gap-2"><Lock size={12}/> Profile Unavailable</p>
          </div>
      );
  }

  return (
    <div className="fixed inset-0 bg-black overflow-y-auto pb-20 animate-in slide-in-from-right z-[250] scrollbar-hide">
      
      {/* ❌ REMOVED: ThriftDetailModal - thrift items now open as regular posts */}

      {/* Normal Post Modal */}
      <ProfilePostModal 
        post={activePost} 
        isOpen={!!activePost} 
        onClose={() => setActivePost(null)} 
        currentUser={currentUser} 
        onSelectProduct={onSelectProduct}
      />

      {actionFeedback && (
          <div className="fixed top-24 left-1/2 -translate-x-1/2 bg-white text-black px-6 py-3 rounded-none border border-black font-bold shadow-2xl z-[300] flex items-center gap-2 animate-in fade-in zoom-in w-max uppercase tracking-widest text-xs">
              <CheckCircle size={16} /> {actionFeedback}
          </div>
      )}

      {isShareSheetOpen && currentUser && (
          <div className="fixed inset-0 z-[300] bg-black/90 backdrop-blur-sm flex items-center justify-center p-6 animate-in fade-in">
              <div className="w-full max-w-sm bg-black border border-zinc-800 p-6 flex flex-col shadow-2xl max-h-[70vh]">
                  <div className="flex items-center justify-between mb-6">
                      <h3 className="text-white font-black text-xl italic uppercase tracking-tighter">Share to...</h3>
                      <button onClick={() => setIsShareSheetOpen(false)} className="p-2 text-zinc-500 hover:text-white"><X size={20}/></button>
                  </div>
                  <div className="relative mb-6">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                      <input placeholder="Search people" className="w-full bg-zinc-950 border border-zinc-800 py-3 pl-12 pr-4 text-white text-sm outline-none focus:border-white transition-colors placeholder:text-zinc-600 uppercase font-mono" value={shareSearch} onChange={(e) => setShareSearch(e.target.value)} autoFocus />
                  </div>
                  <div className="flex-1 overflow-y-auto space-y-2 scrollbar-hide">
                      {filteredShareUsers.length > 0 ? filteredShareUsers.map(user => (
                          <div key={user.uid} className="flex items-center justify-between p-3 hover:bg-zinc-900 border border-transparent hover:border-zinc-800 transition-colors group">
                              <div className="flex items-center gap-4">
                                  <div className="w-10 h-10 bg-zinc-950 overflow-hidden border border-zinc-800 flex items-center justify-center text-zinc-500 font-bold text-lg uppercase rounded-full lg:rounded-none">
                                      {user.photoURL || user.photoUrl || user.avatar ? <img src={user.photoURL || user.photoUrl || user.avatar} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all" alt=""/> : user.displayName?.charAt(0) || 'U'}
                                  </div>
                                  <div><p className="text-white font-black text-xs uppercase tracking-widest">{user.displayName}</p><p className="text-zinc-500 font-mono text-[10px]">@{user.handle || 'user'}</p></div>
                              </div>
                              <button onClick={() => handleSendProfileToUser(user.uid, user.displayName)} className="bg-white text-black px-4 py-2 text-[10px] font-black uppercase tracking-widest hover:bg-zinc-200 transition-colors">Send</button>
                          </div>
                      )) : <div className="text-center py-10 text-zinc-600 text-xs font-bold uppercase tracking-widest">No users found</div>}
                  </div>
              </div>
          </div>
      )}

      {/* TOP NAVIGATION */}
      <div className="sticky top-0 z-[260] bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-4 lg:py-6 flex items-center justify-between w-full">
        <button onClick={onBack} className="p-2 text-white hover:bg-zinc-900 rounded-full lg:rounded-none transition-colors flex items-center gap-2">
          <ArrowLeft size={24} /> <span className="hidden lg:inline font-bold uppercase tracking-widest text-xs">Back</span>
        </button>
        
        <span className="text-base lg:text-xl font-black text-white uppercase tracking-[0.2em] truncate max-w-[200px] lg:max-w-none">
            {profile.displayName || 'Loading...'}
        </span>
        
        <div className="relative flex items-center gap-2 lg:gap-4">
            <button onClick={() => setIsShareSheetOpen(true)} className="hidden lg:flex p-2 text-white hover:text-cyan-400 transition-colors items-center gap-2">
                <Share size={20}/> <span className="font-bold uppercase tracking-widest text-xs">Share</span>
            </button>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="p-2 text-white hover:text-zinc-400 transition-colors">
                {isMenuOpen ? <X size={24} /> : <MoreHorizontal size={24} />}
            </button>

            {isMenuOpen && (
                <div className="absolute right-0 top-12 w-56 bg-black border border-zinc-800 shadow-2xl z-30 animate-in fade-in zoom-in-95 origin-top-right">
                    <button onClick={() => {setIsMenuOpen(false); setIsShareSheetOpen(true);}} className="lg:hidden w-full text-left px-4 py-4 text-xs font-black uppercase tracking-widest text-white hover:bg-zinc-900 flex items-center gap-3 border-b border-zinc-800"><Send size={16} /> Share Profile</button>
                    <button onClick={handleCopyLink} className="w-full text-left px-4 py-4 text-xs font-black uppercase tracking-widest text-white hover:bg-zinc-900 flex items-center gap-3"><Copy size={16} /> Copy Link</button>
                    <div className="h-px bg-zinc-800 w-full"></div>
                    <button onClick={handleBlock} className="w-full text-left px-4 py-4 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-zinc-900 flex items-center gap-3"><Ban size={16} /> Block User</button>
                    <button onClick={handleReport} className="w-full text-left px-4 py-4 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-zinc-900 flex items-center gap-3"><Flag size={16} /> Report</button>
                </div>
            )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto w-full pt-10 px-6">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6 lg:gap-12 mb-12">
            
            {/* PROFILE PICTURE WITH SELLER SPEECH BUBBLE */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full border-2 border-zinc-800 overflow-hidden bg-zinc-900 flex items-center justify-center text-4xl lg:text-5xl font-black text-zinc-500 uppercase">
                  {profile.photoURL || (profile as any).photoUrl || (profile as any).avatar || (profile as any).image ? (
                      <img src={profile.photoURL || (profile as any).photoUrl || (profile as any).avatar || (profile as any).image} referrerPolicy="no-referrer" className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500" alt="" />
                  ) : (
                      profile.displayName ? profile.displayName.charAt(0) : 'U'
                  )}
              </div>
              {/* 🟢 WHITE SPEECH BUBBLE FOR SELLER */}
              {isSeller && (
                <div className="absolute -top-2 -right-2 bg-white text-black rounded-full px-2.5 py-1 text-[9px] font-black uppercase tracking-widest shadow-lg flex items-center gap-1 whitespace-nowrap z-10 after:content-[''] after:absolute after:bottom-[-6px] after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0 after:border-l-[6px] after:border-l-transparent after:border-r-[6px] after:border-r-transparent after:border-t-[6px] after:border-t-white">
                  Seller
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left w-full">
                <h1 className="text-2xl lg:text-4xl font-black text-white uppercase italic tracking-tighter mb-2 flex items-center gap-3 flex-wrap">
                    {profile.displayName || 'Unknown User'}
                    {(profile as any).isBrand && <CheckCircle size={20} className="text-blue-500" fill="currentColor" stroke="black"/>}
                </h1>
                
                <div className="flex items-center justify-center lg:justify-start gap-2 text-zinc-500 font-mono text-[10px] uppercase tracking-widest mb-6">
                    <MapPin size={12} /> {profile.location || 'South Africa'}
                </div>

                <div className="hidden lg:flex items-center gap-10 mb-8 border-y border-zinc-800 py-4 w-full max-w-lg">
                    <div className="text-center"><span className="text-xl font-black text-white block">{followerCount}</span><span className="text-[10px] text-zinc-500 uppercase tracking-widest">Followers</span></div>
                    <div className="text-center"><span className="text-xl font-black text-white block">{followingCount}</span><span className="text-[10px] text-zinc-500 uppercase tracking-widest">Following</span></div>
                    <div className="text-center"><span className="text-xl font-black text-white block">{(profile as any).cred || (profile as any).credits || 0}</span><span className="text-[10px] text-zinc-500 uppercase tracking-widest">CRD</span></div>
                </div>

                <div className="flex lg:hidden justify-center gap-8 mb-8 border-y border-zinc-800 py-4 w-full">
                    <div className="text-center"><span className="text-lg font-black text-white block">{followerCount}</span><span className="text-[9px] text-zinc-500 uppercase tracking-widest">Followers</span></div>
                    <div className="text-center"><span className="text-lg font-black text-white block">{followingCount}</span><span className="text-[9px] text-zinc-500 uppercase tracking-widest">Following</span></div>
                    <div className="text-center"><span className="text-lg font-black text-white block">{(profile as any).cred || (profile as any).credits || 0}</span><span className="text-[9px] text-zinc-500 uppercase tracking-widest">CRD</span></div>
                </div>

                <p className="text-sm text-zinc-400 font-medium leading-relaxed max-w-md mb-8">
                    {profile.bio || "No bio yet."}
                </p>

                <div className="flex gap-4 w-full max-w-md justify-center lg:justify-start">
                    {isFollowing ? (
                        <button onClick={() => onUnfollow(profile.uid)} className="flex-1 py-4 bg-zinc-950 border border-zinc-800 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 hover:border-white transition-colors lg:rounded-none rounded-xl">
                            <UserCheck size={16} /> Following
                        </button>
                    ) : hasRequested ? (
                        <button onClick={handleCancelRequest} className="flex-1 py-4 bg-zinc-950 border border-zinc-800 text-zinc-500 hover:text-white hover:border-white transition-colors font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 lg:rounded-none rounded-xl group">
                            <Lock size={16} className="group-hover:hidden" />
                            <X size={16} className="hidden group-hover:block" />
                            Requested
                        </button>
                    ) : (
                        <button onClick={handleFollowAction} className="flex-1 py-4 bg-white text-black font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 hover:bg-zinc-200 transition-colors lg:rounded-none rounded-xl">
                            <UserPlus size={16} /> {profile.isPrivate ? 'Request' : 'Follow'}
                        </button>
                    )}
                    <button onClick={() => onMessage(profile.uid)} className="flex-1 py-4 bg-zinc-950 border border-zinc-800 text-white font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 hover:border-white transition-colors lg:rounded-none rounded-xl">
                        <MessageCircle size={16} /> Message
                    </button>
                </div>
            </div>
        </div>

        <div className="py-4 border-b border-zinc-800 mb-6 flex justify-center lg:justify-start items-center gap-3">
            <Grid size={16} className="text-white"/>
            <span className="text-xs font-black text-white uppercase tracking-[0.2em]">Closet</span>
        </div>

        <div onClick={() => setIsMenuOpen(false)}>
            {canViewContent ? (
                userPosts.length > 0 ? (
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 lg:gap-4">
                        {renderGridItems(userPosts)}
                    </div>
                ) : (
                    <div className="text-center py-32 opacity-30 flex flex-col items-center">
                        <Grid size={48} strokeWidth={1} className="text-white mb-4" />
                        <p className="text-white font-black uppercase tracking-[0.3em] text-xs">Empty Closet</p>
                    </div>
                )
            ) : (
                <div className="flex flex-col items-center justify-center py-32 opacity-40">
                    <div className="w-24 h-24 border border-zinc-800 rounded-full flex items-center justify-center mb-6 bg-zinc-950">
                        <Lock size={32} strokeWidth={1} className="text-white" />
                    </div>
                    <h3 className="text-xl font-black text-white uppercase tracking-widest mb-2">Private Archive</h3>
                    <p className="text-xs font-mono text-zinc-500 uppercase tracking-widest">Follow to access classified data</p>
                </div>
            )}
        </div>

      </div>
    </div>
  );
};